import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { resolveMedia } from '../../context/SettingsContext';
import AdminSearch from '../../components/admin/AdminSearch';

const productStock = (product) => product.productType === 'variable'
  ? (product.variants || []).filter((variant) => variant.isActive !== false).reduce((total, variant) => total + Number(variant.stock || 0), 0)
  : Number(product.stock || 0);
const productPrice = (product) => {
  const variantPrices = product.productType === 'variable'
    ? (product.variants || []).filter((variant) => variant.isActive !== false).map((variant) => Number(variant.price || 0))
    : [];
  return variantPrices.length ? Math.min(...variantPrices) : Number(product.price || 0);
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const { hasPerm } = useAuth();

  const fetchProducts = useCallback(() => {
    setLoading(true);
    api.get('/products/admin/all', { params: search ? { search } : {} })
      .then((res) => setProducts(res.data))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(fetchProducts, [fetchProducts]);

  const handleStockAdjust = async (id, currentStock, delta) => {
    const newStock = Math.max(0, currentStock + delta);
    try {
      await api.put(`/products/${id}`, { stock: newStock });
      toast.success(`Stock updated to ${newStock}`);
      fetchProducts();
    } catch (e) {
      toast.error('Could not adjust stock');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this product permanently?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (e) {
      toast.error('Could not delete product');
    }
  };

  const exportCSV = () => {
    if (!products.length) {
      toast.error('No products available to export');
      return;
    }
    const headers = ['Product Name', 'Category', 'Price', 'Stock', 'Is BestSeller', 'Status'];
    const rows = products.map((p) => [
      `"${p.name}"`,
      `"${p.category?.name || 'Uncategorized'}"`,
      productPrice(p),
      productStock(p),
      p.isBestSeller ? 'Yes' : 'No',
      p.isActive ? 'Active' : 'Inactive',
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Chandana_Aura_Inventory_Report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Products Inventory CSV Report downloaded');
  };

  return (
    <div className="space-y-6 text-[#2B1810]">
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">Product Catalog & Inventory</h1>
          <p className="text-xs text-[#8A796C]">Manage product listings, pricing, and live inventory stock.</p>
        </div>

        <div className="flex w-full xl:w-auto flex-col sm:flex-row sm:items-center gap-3">
          <AdminSearch value={searchInput} onChange={setSearchInput} onSearch={setSearch} placeholder="Search name, SKU or tag" />
          <button
            onClick={exportCSV}
            className="bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#E8DFD8] text-[#2B1810] font-bold text-xs uppercase tracking-wider px-4 py-2 rounded transition-colors"
          >
            ↓ Export Inventory CSV
          </button>

          {hasPerm('products', 'create') && (
            <Link 
              to="/admin/products/new" 
              className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded shadow transition-colors"
            >
              + Add Product
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E8DFD8] rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-[#F5EFEB] text-[#2B1810] font-heading uppercase text-[11px] font-bold tracking-wider border-b border-[#E8DFD8]">
              <tr>
                <th className="p-4">Product Name</th>
                <th className="p-4">Type</th>
                <th className="p-4">Price</th>
                <th className="p-4">Stock Adjuster</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8DFD8]">
              {loading ? (
                <tr><td className="p-6 text-center text-[#8A796C]" colSpan={6}>Loading product catalog...</td></tr>
              ) : products.length === 0 ? (
                <tr><td className="p-6 text-center text-[#8A796C]" colSpan={6}>No products found.</td></tr>
              ) : (
                products.map((p) => (
                  <tr key={p._id} className="hover:bg-[#FAF8F5] transition-colors">
                    <td className="p-4 font-bold text-[#2B1810]">
                      <div className="flex items-center gap-3">
                        <img 
                          src={p.images?.[0] ? resolveMedia(p.images[0]) : '/assets/ganesha_idol.jpg'} 
                          alt="" 
                          className="w-10 h-10 object-cover rounded border border-[#E8DFD8] shrink-0" 
                          onError={(e) => { e.target.src = '/assets/ganesha_idol.jpg'; }}
                        />
                        <div>
                          <span>{p.name}</span>
                          {p.isBestSeller && (
                            <span className="block text-[9px] text-[#C49A45] font-bold uppercase">BEST SELLER</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 uppercase text-[#8A796C] font-semibold">{p.productType}</td>
                    <td className="p-4 font-bold text-[#2B1810]">{p.productType === 'variable' && <span className="mr-1 text-[9px] font-normal text-[#8A796C]">From</span>}₹{productPrice(p).toFixed(2)}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-xs ${productStock(p) < 10 ? 'text-red-600' : 'text-[#2B1810]'}`}>
                          {productStock(p)} units
                        </span>
                        {p.productType === 'variable' ? hasPerm('products', 'edit') && <Link to={`/admin/products/${p._id}`} className="rounded border border-[#D9D0C8] px-2 py-1 text-[9px] font-bold text-[#7F5D1E]">Variants</Link> : <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleStockAdjust(p._id, p.stock || 0, -10)}
                            className="w-6 h-6 rounded bg-[#F5EFEB] hover:bg-[#E8DFD8] border border-[#E8DFD8] font-bold text-xs text-[#2B1810]"
                            title="Decrease 10 stock"
                          >
                            -
                          </button>
                          <button
                            onClick={() => handleStockAdjust(p._id, p.stock || 0, 10)}
                            className="w-6 h-6 rounded bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#C49A45] font-bold text-xs text-[#C49A45]"
                            title="Add 10 stock"
                          >
                            +
                          </button>
                        </div>}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                        p.isActive ? 'bg-[#E8F3EB] text-[#2E5E3E]' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-right space-x-2">
                      {hasPerm('products', 'edit') && (
                        <Link 
                          to={`/admin/products/${p._id}`} 
                          className="bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#E8DFD8] text-[#2B1810] font-bold text-[11px] py-1.5 px-3 rounded transition-colors"
                        >
                          Edit
                        </Link>
                      )}
                      {hasPerm('products', 'delete') && (
                        <button 
                          onClick={() => remove(p._id)} 
                          className="bg-red-50 hover:bg-red-100 text-red-700 font-bold text-[11px] py-1.5 px-3 rounded transition-colors"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
